import type {
  ClassificationEvent,
  CriterionEvent,
  CriterionDetail,
  CriterionScalar,
  TraceEvent,
} from "@/lib/previewDecisions";

/**
 * The Anxiety view: classification and criterion-threshold events grouped by
 * the anxiety unit they belong to, as a step/threshold table.
 *
 * It computes NOTHING clinical. Every observed value, operator, threshold and
 * verdict is the producer's own, carried from its criterion event; this module
 * only decides which unit an event belongs to and keeps the latest evaluation
 * of each step. The producer's identity convention is the join key: a
 * criterion is `<classifierId>.<step>` and `<classifierId>.diagnosis` is the
 * unit's verdict, so a unit is read off the classifier id's prefix.
 */

export const ANXIETY_UNITS = [
  "panic",
  "gad",
  "social_phobia",
  "agoraphobia",
  "unspecified_anxiety",
  "anxiety_screening",
] as const;

export type AnxietyUnit = (typeof ANXIETY_UNITS)[number];

export const ANXIETY_UNIT_LABELS = {
  panic: "Panic",
  gad: "Generalized anxiety (GAD)",
  social_phobia: "Social phobia",
  agoraphobia: "Agoraphobia",
  unspecified_anxiety: "Unspecified anxiety",
  anxiety_screening: "Anxiety screening",
} as const satisfies Record<AnxietyUnit, string>;

/** The producer's verdict criterion: `<classifierId>.diagnosis`. */
const VERDICT_STEP = "diagnosis";

/**
 * The unit a classifier or criterion id belongs to, or null.
 *
 * An id belongs to a unit when it IS the unit's name or continues it past a
 * `_` or `.` boundary (`panic`, `panic_classification`, `panic.diagnosis`), so
 * `gad` never claims a hypothetical `gadget`.
 */
export function anxietyUnitOf(id: string): AnxietyUnit | null {
  return (
    ANXIETY_UNITS.find(
      (unit) =>
        id === unit || id.startsWith(`${unit}_`) || id.startsWith(`${unit}.`),
    ) ?? null
  );
}

export type AnxietyStepRow = {
  criterionId: string;
  /** The criterion id past its classifier prefix: the step a clinician reads. */
  step: string;
  isVerdict: boolean;
  observed: CriterionScalar | null;
  operator: CriterionDetail["operator"];
  threshold: CriterionScalar | null;
  passed: boolean;
  /** The turn of the LATEST evaluation of this step, or null when it carried none. */
  turnNumber: number | null;
  sequence: number;
  /** How many times the run evaluated this step. */
  evaluations: number;
};

export type AnxietyUnitView = {
  unit: AnxietyUnit;
  /** Classification events recorded for this unit. */
  classifications: number;
  lastClassificationTurn: number | null;
  /** The latest evaluation of each step, in the order the steps first appeared. */
  steps: readonly AnxietyStepRow[];
};

function stepOf(criterionId: string): string {
  const dot = criterionId.indexOf(".");
  return dot === -1 ? criterionId : criterionId.slice(dot + 1);
}

/**
 * Groups the readable events by anxiety unit. Only units the run touched are
 * returned, in the fixed `ANXIETY_UNITS` order, so the table's layout does not
 * depend on which unit happened to be classified first.
 */
export function buildAnxietyView(
  events: readonly TraceEvent[],
): AnxietyUnitView[] {
  const ordered = [...events].sort((a, b) => a.sequence - b.sequence);
  const units = new Map<
    AnxietyUnit,
    {
      classifications: number;
      lastClassificationTurn: number | null;
      steps: Map<string, AnxietyStepRow>;
    }
  >();
  const slot = (unit: AnxietyUnit) => {
    let found = units.get(unit);
    if (!found) {
      found = {
        classifications: 0,
        lastClassificationTurn: null,
        steps: new Map(),
      };
      units.set(unit, found);
    }
    return found;
  };

  for (const event of ordered) {
    if (event.family === "classification") {
      const classification: ClassificationEvent = event;
      const unit = anxietyUnitOf(classification.detail.classifierId);
      if (unit !== null) {
        const entry = slot(unit);
        entry.classifications += 1;
        entry.lastClassificationTurn = classification.coordinate.turnNumber;
      }
    } else if (event.family === "criterion_threshold") {
      const criterion: CriterionEvent = event;
      const unit = anxietyUnitOf(criterion.detail.criterionId);
      if (unit !== null) {
        const steps = slot(unit).steps;
        const previous = steps.get(criterion.detail.criterionId);
        const step = stepOf(criterion.detail.criterionId);
        steps.set(criterion.detail.criterionId, {
          criterionId: criterion.detail.criterionId,
          step,
          isVerdict: step === VERDICT_STEP,
          observed: criterion.detail.observedValue,
          operator: criterion.detail.operator,
          threshold: criterion.detail.threshold,
          passed: criterion.detail.passed,
          turnNumber: criterion.coordinate.turnNumber,
          sequence: criterion.sequence,
          evaluations: (previous?.evaluations ?? 0) + 1,
        });
      }
    }
  }

  return ANXIETY_UNITS.flatMap((unit) => {
    const entry = units.get(unit);
    return entry
      ? [
          {
            unit,
            classifications: entry.classifications,
            lastClassificationTurn: entry.lastClassificationTurn,
            // Map preserves first-insertion order, and `set` on an existing key
            // keeps its place: steps stay where they first appeared.
            steps: [...entry.steps.values()],
          },
        ]
      : [];
  });
}
